import { useEffect, useRef } from 'react';
import type { DayHeatmap } from '@busy/api';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import type { ForecastDay } from '../../lib/weather';
import type { ForecastDayPlan, ForecastConfidence } from '../../lib/useCapacityForecast';
import { WeatherChip } from './WeatherChip';

// Tap-a-row popover for the unified capacity heatmap. Two flavors:
//
//   - PAST row → shows the actual day stats (utilization %, cuts, hours
//     worked) plus the historical weather for that date.
//   - FORECAST row → shows the forecast detail (expected cuts band,
//     revenue band, walk-in driven flag, confidence pill) plus the
//     forecasted weather. Replaces the standalone CapacityForecast
//     cards which lived between the heatmap and the source-mix tile
//     in the original layout.
//
// Both flavors render in the SAME popover component so the eye doesn't
// switch contexts when paging across past + future rows. Layout +
// chrome are identical; only the body section swaps.

const HOUR_START = 6;

export function HeatmapRowPopover({
  row,
  anchor,
  weather,
  hourlyTargetPct,
  onClose,
}: {
  row: DayHeatmap & {
    is_forecast?: boolean;
    forecast?: ForecastDayPlan;
  };
  /** Bounding rect of the tapped row — used to anchor the popover
   *  above OR below the row depending on viewport space. */
  anchor: DOMRect;
  /** Optional weather for the row's date. Pulled from the parent's
   *  weatherByDate map so the popover doesn't have to fetch it. */
  weather?: ForecastDay | null;
  /** Realism dial — passed to the actual-day stats so the formatted
   *  hours line up with the heatmap's color buckets. */
  hourlyTargetPct: number;
  onClose: () => void;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';

  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click. Tracked via a backdrop button so we don't
  // need a document-level listener.
  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // Position: try below the row first, flip above when not enough room.
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const spaceBelow = viewportH - anchor.bottom;
  const placeBelow = spaceBelow > 220;
  const top = placeBelow ? anchor.bottom + 8 : Math.max(8, anchor.top - 220);
  const left = Math.max(8, Math.min(anchor.left, (typeof window !== 'undefined' ? window.innerWidth : 400) - 312));

  const date = new Date(row.day_ms);
  const dateLine = date.toLocaleDateString(dateLocale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      {/* Backdrop — invisible but captures clicks to dismiss. Lives
          on z-30 so the popover (z-40) sits above it but everything
          else underneath. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default"
      />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        className="fixed z-40 w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-line bg-surface shadow-xl p-4 space-y-3"
        style={{ top, left }}
      >
        {/* Header — day name + weather chip on the right */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
              {row.is_forecast
                ? t('dash.capacity.rowDetail.forecastEyebrow')
                : t('dash.capacity.rowDetail.actualEyebrow')}
            </div>
            <div className="text-[14px] font-bold text-ink-900 dark:text-ink-100 mt-0.5">
              {dateLine}
            </div>
          </div>
          {weather && <WeatherChip day={weather} size="md" />}
        </div>

        {row.is_forecast && row.forecast ? (
          <ForecastBody plan={row.forecast} />
        ) : (
          <ActualBody row={row} hourlyTargetPct={hourlyTargetPct} />
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full h-9 rounded-lg bg-bg-alt text-ink-700 text-sm font-semibold hover:bg-bg-alt/70 transition-colors"
        >
          {t('dash.capacity.rowDetail.close')}
        </button>
      </div>
    </>
  );
}

// ────────── Forecast body ──────────

function ForecastBody({ plan }: { plan: ForecastDayPlan }) {
  const t = useT();
  return (
    <div className="space-y-2">
      {/* Expected cuts band */}
      <DetailRow
        label={t('dash.capacity.rowDetail.expectedCuts')}
        value={
          plan.confidence === 'low'
            ? t('dash.capacity.rowDetail.lowConfidence')
            : plan.visitsLow === plan.visitsHigh
              ? `${plan.visitsLow}`
              : `${plan.visitsLow}–${plan.visitsHigh}`
        }
      />
      {/* Expected revenue */}
      {plan.confidence !== 'low' && (
        <DetailRow
          label={t('dash.capacity.rowDetail.expectedRevenue')}
          value={formatRevenueBand(plan.revenueLowCents, plan.revenueHighCents)}
          accent="positive"
        />
      )}
      {/* Expected hours */}
      {plan.confidence !== 'low' && (
        <DetailRow
          label={t('dash.capacity.rowDetail.expectedHours')}
          value={t('dash.capacity.rowDetail.expectedHoursValue', {
            hours: plan.expectedHours.toFixed(1).replace(/\.0$/, ''),
          })}
        />
      )}
      {/* Confidence pill + walk-in tag — small status row */}
      <div className="flex items-center gap-1.5 flex-wrap pt-1">
        <ConfidencePill confidence={plan.confidence} />
        {plan.walkinDriven && plan.confidence !== 'low' && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.08em] font-extrabold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {t('dash.capacity.forecast.walkinDriven')}
          </span>
        )}
      </div>
      {/* Footnote line — explains the model briefly. Concise so the
          popover stays compact. */}
      <p className="text-[11px] text-ink-500 leading-snug pt-1">
        {plan.confidence === 'low'
          ? t('dash.capacity.rowDetail.lowConfidenceHint')
          : t('dash.capacity.rowDetail.forecastHint', { n: plan.sampleSize })}
      </p>
    </div>
  );
}

// ────────── Actual body ──────────

function ActualBody({
  row,
  hourlyTargetPct,
}: {
  row: DayHeatmap;
  hourlyTargetPct: number;
}) {
  const t = useT();

  // Aggregate stats from the hourly row. The percentages stored in
  // row.hours are "% of an hour fully utilized at the current target,"
  // so summing them and dividing by 100 gives total hours worked.
  const hoursWorked =
    row.hours.reduce((s, p) => s + p, 0) / 100 *
    // Convert "% of hourly_target_pct minute capacity" into actual hours
    // by un-scaling: each 100% cell = (hourly_target_pct ÷ 100) hours of
    // cutting time. We multiply back by that factor.
    (hourlyTargetPct / 100);
  const activeHours = row.hours.filter((p) => p > 0).length;
  // Peak hour: the hour with the highest utilization. Stored as 12hr
  // clock so the popover reads "2 PM peak" rather than "14:00 peak."
  let peakIdx = -1;
  let peakPct = 0;
  row.hours.forEach((p, i) => {
    if (p > peakPct) {
      peakPct = p;
      peakIdx = i;
    }
  });
  const peakHourLabel = peakIdx >= 0 ? format12Hour(HOUR_START + peakIdx) : null;

  return (
    <div className="space-y-2">
      <DetailRow
        label={t('dash.capacity.rowDetail.hoursWorked')}
        value={t('dash.capacity.rowDetail.hoursWorkedValue', {
          hours: hoursWorked.toFixed(1).replace(/\.0$/, ''),
        })}
      />
      <DetailRow
        label={t('dash.capacity.rowDetail.activeHours')}
        value={t('dash.capacity.rowDetail.activeHoursValue', { n: activeHours })}
      />
      {peakHourLabel && peakPct > 0 && (
        <DetailRow
          label={t('dash.capacity.rowDetail.peakHour')}
          value={t('dash.capacity.rowDetail.peakHourValue', {
            hour: peakHourLabel,
            pct: Math.round(peakPct),
          })}
        />
      )}
    </div>
  );
}

// ────────── shared bits ──────────

function DetailRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'positive';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-ink-500">{label}</span>
      <span
        className={`font-bold tabular-nums text-[13px] ${
          accent === 'positive' ? 'text-green-700 dark:text-green-300' : 'text-ink-900 dark:text-ink-100'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function ConfidencePill({ confidence }: { confidence: ForecastConfidence }) {
  const t = useT();
  const cls =
    confidence === 'high'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
      : confidence === 'medium'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-bg-alt text-ink-500';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.08em] font-extrabold ${cls}`}>
      {t(`dash.capacity.forecast.confidence.${confidence}`)}
    </span>
  );
}

function formatRevenueBand(lowCents: number, highCents: number): string {
  const lowD = Math.round(lowCents / 100);
  const highD = Math.round(highCents / 100);
  if (lowD === highD) return `$${lowD}`;
  return `$${lowD}–$${highD}`;
}

function format12Hour(hour24: number): string {
  const h = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const ampm = hour24 < 12 ? 'AM' : 'PM';
  return `${h} ${ampm}`;
}
